import { BaseClient } from '../base-client';
import type {
  BulkInvoiceResult,
  CreateInvoiceInput,
  GenerationRunInput,
  ImportInvoicesResult,
  Invoice,
  InvoiceApproveTarget,
  InvoiceAttachment,
  InvoiceBulkSendQueued,
  InvoiceBulkSendResult,
  InvoiceBulkTarget,
  InvoiceDeliveryReceipt,
  InvoiceFlag,
  InvoiceGenerationRun,
  InvoiceGenerationRunItem,
  InvoiceLineAllocation,
  InvoiceLineItem,
  InvoiceListQuery,
  InvoiceRunItemsQuery,
  InvoiceRunListQuery,
  InvoiceRunPreview,
  InvoiceRunProgress,
  InvoiceScheduleSend,
  InvoiceSendChoice,
  InvoiceSendConfig,
  InvoiceStatusCounts,
  NewInvoiceBillTo,
  NewInvoiceDefaults,
  PatchInvoiceInput,
  UnbilledCustomer,
} from './types';

/** Single-invoice writes return the header plus its lines, because nothing
 *  useful can be done with one and not the other. */
type One = {
  data: Invoice;
  related: { lineItems: InvoiceLineItem[]; allocations: InvoiceLineAllocation[] };
};
/** The single READ additionally carries the review flags, so the detail page
 *  can render the review panel above the line items without a second round
 *  trip that would pop the warning in after the thing it warns about. */
type OneWithFlags = {
  data: Invoice;
  related: {
    lineItems: InvoiceLineItem[];
    /** Where each line is booked — see `InvoiceLineAllocation`. */
    allocations: InvoiceLineAllocation[];
    flags: InvoiceFlag[];
    /** The customer's stored billing address, as held — see `NewInvoiceBillTo`. */
    billingAddress: unknown;
    /** The tenant's current email body template — what `data.emailBody: null`
     *  is sent with. */
    emailBodyTemplate: string;
    /** Likewise the subject line it is sent under. */
    emailSubjectTemplate: string;
  };
};
type FlagsResponse = { data: InvoiceFlag[] };
type ListResponse = { data: Invoice[]; meta: { total: number; limit: number; offset: number } };
type IdsResponse = { data: string[]; meta: { total: number } };
type BulkResponse = { data: BulkInvoiceResult };
type NewDefaultsResponse = { data: NewInvoiceDefaults };
type NewBillToResponse = { data: NewInvoiceBillTo };
type StatusCountsResponse = { data: InvoiceStatusCounts };
type ImportResponse = { data: ImportInvoicesResult };

const BASE = '/api/invoices';

export class InvoicesClient extends BaseClient {
  list(query: InvoiceListQuery = {}): Promise<ListResponse> {
    return this.request(BASE, { query: normalize(query) });
  }

  /**
   * Every id matching a filter, without the rows.
   *
   * For callers that must enumerate a matched set. The bulk bar does not: it
   * passes the filter straight to `approve`, which resolves it server-side at
   * the moment of the write.
   */
  ids(query: Omit<InvoiceListQuery, 'limit' | 'offset'> = {}): Promise<IdsResponse> {
    return this.request(`${BASE}/ids`, { query: normalize(query) });
  }

  /**
   * How many matching invoices sit in each status.
   *
   * What the bulk bar quotes before it acts: a selection of "everything
   * matching these filters" spans statuses, and only some of them can be
   * approved. Same filter as `list`, so the numbers describe the same set.
   */
  statusCounts(
    query: Omit<InvoiceListQuery, 'limit' | 'offset'> = {},
  ): Promise<StatusCountsResponse> {
    return this.request(`${BASE}/status-counts`, { query: normalize(query) });
  }

  get(id: string): Promise<OneWithFlags> {
    return this.request(`${BASE}/${encodeURIComponent(id)}`);
  }

  /**
   * One invoice's review flags.
   *
   * `get` already carries these; this exists for the LIST, whose popover opens
   * on a single row and has no use for that invoice's line items. Render them
   * through `describeInvoiceFlag` from `@clockwise/shared` rather than
   * composing prose at the call site — one flag must not read three ways.
   */
  flags(id: string): Promise<FlagsResponse> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/flags`);
  }

  /**
   * Flags for a PAGE of invoices, in one round trip.
   *
   * What the list column and the severity stripe read. Per-row calls would be
   * one request per visible invoice to draw a single column, and the stripe
   * cannot be coloured from the cached count alone — it needs to know whether
   * the loudest flag is blocking or merely informational.
   *
   * Returns `[]` for an empty id list without touching the network: a page
   * that has not loaded yet is not a reason to ask about nothing.
   */
  flagsForMany(invoiceIds: readonly string[]): Promise<FlagsResponse> {
    if (invoiceIds.length === 0) return Promise.resolve({ data: [] });
    return this.request(`${BASE}/flags`, { query: { invoiceIds: invoiceIds.join(',') } });
  }

  /**
   * The tenant-level half of what the create screen needs to draw an invoice
   * before one exists — issuer, remittance, defaults and the expected number.
   * No customer required, so this can be fetched the instant the screen opens.
   * The server resolves it exactly as `create` would, so the preview and the
   * saved document cannot disagree.
   */
  newDefaults(): Promise<NewDefaultsResponse> {
    return this.request(`${BASE}/new-defaults`);
  }

  /**
   * The customer-specific half: the Bill To block a new invoice for this
   * customer would snapshot right now.
   */
  newBillTo(customerId: string): Promise<NewBillToResponse> {
    return this.request(`${BASE}/new-defaults/bill-to`, { query: { customerId } });
  }

  /**
   * Raise an invoice. The number is allocated server-side.
   *
   * Pass `idempotencyKey` on anything that might be retried: without it a
   * retried create burns a second invoice number and raises a second document.
   */
  create(input: CreateInvoiceInput, opts: { idempotencyKey?: string } = {}): Promise<One> {
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.request(BASE, { method: 'POST', body: input, headers });
  }

  /** Edit in place. Sending `lineItems` REPLACES the whole collection.
   *  Rejected once the invoice is `sent`. */
  patch(id: string, input: PatchInvoiceInput): Promise<One> {
    return this.request(`${BASE}/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
  }

  /** Drafts only — an approved invoice was signed off and a sent one is in a
   *  customer's inbox. */
  remove(id: string): Promise<{ data: Invoice }> {
    return this.request(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE', body: {} });
  }

  /**
   * Approve — a list of ids, or everything matching a filter.
   *
   * Always bulk, even from a single-invoice screen, so there is one
   * implementation of what approving means. The filter form is resolved to rows
   * on the server at the moment of the write: pass the same filter the list is
   * showing and it acts on every match, including the pages nobody opened.
   *
   * Partial success is normal: check `failed` for the rows that were skipped
   * and why, rather than assuming a 200 approved everything you sent.
   */
  approve(target: InvoiceApproveTarget): Promise<BulkResponse> {
    return this.request(`${BASE}/approve`, { method: 'POST', body: target });
  }

  /** Unlock ONE approved invoice — back to draft, approval cleared. There is
   *  no bulk form: each unlock is a decision about one document. Unreachable
   *  once sent — that is the one state this cannot walk back. */
  unapprove(id: string): Promise<BulkResponse> {
    return this.request(`${BASE}/unapprove`, { method: 'POST', body: { ids: [id] } });
  }

  /**
   * Change when invoices get emailed, over ids or a filter. Drafts and approved
   * invoices alike — scheduling never unlocks an approval. Sent invoices, and
   * rows whose invoice date falls after the chosen date, come back in `failed`.
   */
  scheduleSend(target: InvoiceBulkTarget, schedule: InvoiceScheduleSend): Promise<BulkResponse> {
    return this.request(`${BASE}/schedule-send`, {
      method: 'POST',
      body: { target, ...schedule },
    });
  }

  /**
   * Set the invoice date, over ids or a filter. Drafts only — the date is
   * content, so approved (locked) and sent invoices come back in `failed`, as
   * do drafts whose send date would fall before the new date. Each invoice's
   * due date moves with it, keeping its payment window.
   */
  setInvoiceDate(
    target: InvoiceBulkTarget,
    invoiceDate: string,
    opts: { dryRun?: boolean } = {},
  ): Promise<BulkResponse> {
    return this.request(`${BASE}/invoice-date`, {
      method: 'POST',
      body: opts.dryRun ? { target, invoiceDate, dryRun: true } : { target, invoiceDate },
    });
  }

  /** Record that invoices went out. `sentAt` is for a send that already
   *  happened out of band — omit it and the server stamps now. */
  markSent(ids: readonly string[], sentAt?: string): Promise<BulkResponse> {
    return this.request(`${BASE}/mark-sent`, { method: 'POST', body: { ids, sentAt } });
  }

  /**
   * The rendered document as a PDF blob, built from the invoice's own stored
   * snapshot. Works on a draft — that is what makes it a preview.
   *
   * Nothing is uploaded; the bytes are rendered per request. Callers that want
   * a durable, linkable copy want the send path, not this.
   */
  pdf(id: string): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob(`${BASE}/${encodeURIComponent(id)}/pdf`);
  }

  /** Download the filtered invoices as XLSX — one row per line item, header
   *  fields repeated. The filter mirrors the list page exactly. */
  exportXlsx(
    filter: Omit<InvoiceListQuery, 'limit' | 'offset'> = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob(`${BASE}/export`, { query: normalize(filter) });
  }

  /** Download a blank XLSX template with just the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob(`${BASE}/import/template`);
  }

  /**
   * Upload an .xlsx or .csv file. Rows are grouped by `Invoice #`; each group
   * becomes one DRAFT invoice carrying the number from the file.
   *
   * Import creates and never updates — a row whose number already exists is
   * reported as a conflict and skipped, because matching on invoice number
   * would let a re-uploaded file silently rewrite a document already sent.
   */
  /**
   * Email the invoice now — PDF and attachments — to its contacts (To and CC),
   * to additional addresses, or both. Approved and sent invoices; only an
   * approved invoice's first send to its contacts marks it sent. A refusal
   * carries the reason (no billing email, too large for one email, already
   * sending). `delivery` says where it actually went. Pass one idempotency
   * key per press so a retry cannot send it twice.
   */
  send(
    id: string,
    choice: InvoiceSendChoice,
    opts: { idempotencyKey?: string } = {},
  ): Promise<One & { delivery: InvoiceDeliveryReceipt }> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/send`, {
      method: 'POST',
      body: choice,
      ...(opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {}),
    });
  }

  /**
   * Which of these invoices a bulk send would email (each to its own
   * contacts) and which it would skip and why. Changes nothing.
   */
  previewBulkSend(target: InvoiceBulkTarget): Promise<{ data: InvoiceBulkSendResult }> {
    return this.request(`${BASE}/send-bulk`, {
      method: 'POST',
      body: { target, dryRun: true },
    });
  }

  /**
   * Email many invoices, each to its own contacts, in the background.
   * Approved invoices get their first send; sent ones are resent. Answers at
   * once with how many were queued and what was skipped. Pass one
   * idempotency key per press so a retry cannot queue them twice.
   */
  queueBulkSend(
    target: InvoiceBulkTarget,
    opts: { idempotencyKey?: string } = {},
  ): Promise<{ data: InvoiceBulkSendQueued }> {
    return this.request(`${BASE}/send-bulk`, {
      method: 'POST',
      body: { target },
      ...(opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {}),
    });
  }

  /**
   * Where a send would actually go. Asked of the API, not read from the web's
   * own config, so the dialog cannot promise a destination the sender does not
   * use.
   */
  sendConfig(): Promise<{ data: InvoiceSendConfig }> {
    return this.request(`${BASE}/send-config`);
  }

  // ---- Attachments --------------------------------------------------------

  /** Every file attached to the invoice, oldest first. */
  listAttachments(id: string): Promise<{ data: InvoiceAttachment[] }> {
    return this.request(`${BASE}/${encodeURIComponent(id)}/attachments`);
  }

  /**
   * Attach one file. The server decides its stored type from the extension
   * and refuses anything outside the allowed list, over 10 MB, or past 20 per
   * invoice — with a field error on `file`. Pass an idempotency key so a
   * retried upload attaches it once.
   */
  uploadAttachment(
    id: string,
    file: File,
    opts: { idempotencyKey?: string } = {},
  ): Promise<{ data: InvoiceAttachment }> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart(`${BASE}/${encodeURIComponent(id)}/attachments`, fd, { headers });
  }

  /** The file's bytes, always served as a download. */
  downloadAttachment(
    id: string,
    attachmentId: string,
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob(
      `${BASE}/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
    );
  }

  /** Remove an attachment. Refused (409) once the invoice has been sent. */
  deleteAttachment(id: string, attachmentId: string): Promise<{ data: InvoiceAttachment }> {
    return this.request(
      `${BASE}/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    );
  }

  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart(`${BASE}/import`, fd, { headers });
  }

  // ---- Generation runs ----------------------------------------------------

  /** What a run WOULD do. No writes — safe to call on every keystroke of the
   *  dialog, and the only honest way to show a count before committing. */
  previewRun(input: GenerationRunInput): Promise<{ data: InvoiceRunPreview }> {
    return this.request(`${BASE}/runs/preview`, { method: 'POST', body: input });
  }

  /**
   * Start a run. Returns 202 with the run id — the invoices are raised
   * asynchronously, so this resolves long before they exist.
   *
   * `data` is null when nothing was eligible, which is an answer rather than an
   * error; the caller has already seen it in the preview. A 409 means a run is
   * already in progress for the tenant.
   */
  startRun(
    input: GenerationRunInput,
    opts: { idempotencyKey?: string } = {},
  ): Promise<{ data: { runId: string } | null }> {
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.request(`${BASE}/runs`, { method: 'POST', body: input, headers });
  }

  listRuns(
    query: InvoiceRunListQuery = {},
  ): Promise<{ data: InvoiceGenerationRun[]; meta: { total: number } }> {
    return this.request(`${BASE}/runs`, { query: normalize(query) });
  }

  getRun(runId: string): Promise<{ data: InvoiceGenerationRun }> {
    return this.request(`${BASE}/runs/${runId}`);
  }

  listRunItems(
    runId: string,
    query: InvoiceRunItemsQuery = {},
  ): Promise<{ data: InvoiceGenerationRunItem[]; meta: { total: number } }> {
    return this.request(`${BASE}/runs/${runId}/items`, { query: normalize(query) });
  }

  /** Counts only. Separate from `getRun` because the toast polls it every few
   *  seconds while a run is live. */
  runProgress(runId: string): Promise<{ data: InvoiceRunProgress }> {
    return this.request(`${BASE}/runs/${runId}/progress`);
  }

  /** Who has no invoice for a month, and why. Keyed on the month rather than a
   *  run: a customer that failed in the cron and again in a manual re-run is
   *  one problem, not two. */
  unbilled(month: string): Promise<{ data: UnbilledCustomer[] }> {
    return this.request(`${BASE}/unbilled`, { query: { month } });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    // Id filters go over the wire comma-joined, matching the server's
    // `uuidList`. An empty array is dropped rather than sent as `''`, which
    // the server would reject — "nothing selected" means no filter at all.
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v.join(',');
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
